import React from 'react';
import { useSearchResultsActions, useSearchResultsSelectedFilters } from '@sitecore-search/react';
import { useSearchContext } from '../search-provider/search-context';

const SearchTabs = () => {
    const { filterItems, setActiveFilter } = useSearchContext();
    const { onFacetClick, onClearFilters, onPageNumberChange } = useSearchResultsActions();
    const selectedFilters = useSearchResultsSelectedFilters();

    // Function to remove page parameter from URL
    const removePageFromUrl = () => {
        if (typeof window !== 'undefined') {
            const url = new URL(window.location);
            const searchParams = new URLSearchParams(url.search);
            searchParams.delete('page');
            const newUrl = `${url.pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
            window.history.replaceState({}, '', newUrl);
        }
    };

    const selectedContentType = selectedFilters.filter(facet => facet.facetId === 'type');

    // Check if any filter is currently active
    const hasActiveFilters = selectedContentType && selectedContentType.length > 0;

    const handleAllResultsClick = () => {
        if (hasActiveFilters) {
            onClearFilters();
        }
        // Reset page to 1 and remove page from URL
        onPageNumberChange({ page: 1 });
        removePageFromUrl();
    };

    const handleKeyDown = e => {
        const tabs = document.querySelectorAll('[role="tab"]');
        const currentIndex = Array.from(tabs).indexOf(e.target);

        switch (e.key) {
            case 'ArrowRight':
            case 'ArrowDown': {
                e.preventDefault();
                const nextIndex = (currentIndex + 1) % tabs.length;
                tabs[nextIndex].focus();
                break;
            }
            case 'ArrowLeft':
            case 'ArrowUp': {
                e.preventDefault();
                const prevIndex = currentIndex === 0 ? tabs.length - 1 : currentIndex - 1;
                tabs[prevIndex].focus();
                break;
            }
            case 'Home':
                e.preventDefault();
                tabs[0].focus();
                break;
            case 'End':
                e.preventDefault();
                tabs[tabs.length - 1].focus();
                break;
            default:
                break;
        }
    };

    return (
        <div className="search-tabs-block">
            <div
                className="search-tabs"
                role="tablist"
                aria-label="Search result filters"
            >
                <div className="search-tab">
                    <button
                        type="button"
                        className={`tab-item ${!hasActiveFilters ? 'active' : ''}`}
                        onClick={handleAllResultsClick}
                        onKeyDown={handleKeyDown}
                        role="tab"
                        aria-selected={!hasActiveFilters}
                        aria-controls="search-results-panel"
                        id="tab-all-results"
                        aria-label="Show all search results"
                        tabIndex={!hasActiveFilters ? 0 : -1}
                    >
                        All Results
                    </button>
                </div>
                {filterItems.length > 0 && filterItems.map(tab => {
                    const isActive = selectedContentType.some(facet => facet.facetValueId === tab.id);

                    const handleTabClick = () => {
                        // First, clear all existing type filters by setting them to false
                        setActiveFilter(tab.id);
                        selectedContentType.forEach(activeFacet => {
                            if (activeFacet.facetValueId !== tab.id) {
                                onFacetClick({
                                    facetId: 'type',
                                    facetValueId: activeFacet.facetValueId,
                                    checked: false,
                                    type: 'text',
                                    facetValueIndex: -1,
                                    facetIndex: -1
                                });
                            }
                        });

                        // Then set the clicked item to true (only if it's not already active)
                        if (!isActive) {
                            onFacetClick({
                                facetId: 'type',
                                facetValueId: tab.id,
                                checked: true,
                                type: 'text',
                                facetValueIndex: -1,
                                facetIndex: -1
                            });
                        }

                        // Reset page to 1 and remove page from URL
                        onPageNumberChange({ page: 1 });
                        removePageFromUrl();
                    };

                    return (
                        <div key={tab.id} className="search-tab">
                            <button
                                type="button"
                                className={`tab-item${isActive ? ' active' : ''}`}
                                onClick={handleTabClick}
                                onKeyDown={handleKeyDown}
                                role="tab"
                                aria-selected={isActive}
                                aria-controls="search-results-panel"
                                id={`tab-${tab.id}`}
                                aria-label={`Filter results by ${tab.label}`}
                                tabIndex={isActive ? 0 : -1}
                            >
                                {tab.label}
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default SearchTabs;
